<template>
  <v-navigation-drawer
    v-model="drawer"
    fixed
    right
    temporary
    width="600"
    class="pa-0 firm-details-drawer"
  >
    <template v-if="loading">
      <v-skeleton-loader
        type="article, table-heading, table-thead, table-tbody"
        class="pa-6"
      />
    </template>
    <template v-else-if="firm">
      <v-toolbar flat color="primary" dark>
        <v-toolbar-title>Firm Details: {{ firm.FirstName }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="drawer = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-tabs v-model="activeTab" grow color="primary">
        <v-tab>Profile</v-tab>
        <v-tab>Application</v-tab>
        <v-tab>Finance</v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <!-- Profile Tab -->
        <v-tab-item>
          <v-card flat class="pa-6">
            <v-list two-line subheader>
              <v-subheader>General Information</v-subheader>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle>Firm Name</v-list-item-subtitle>
                  <v-list-item-title class="font-weight-bold">{{
                    firm.FirstName
                  }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle>Registration No</v-list-item-subtitle>
                  <v-list-item-title>{{
                    firm.MemberNo || "N/A"
                  }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle>KRA PIN</v-list-item-subtitle>
                  <v-list-item-title>{{
                    firm.IdentificationDocumentNo || "N/A"
                  }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="my-2"></v-divider>
              <v-subheader>Contact Details</v-subheader>
              <v-list-item>
                <v-list-item-icon
                  ><v-icon color="primary">mdi-email</v-icon></v-list-item-icon
                >
                <v-list-item-content>
                  <v-list-item-subtitle>Email Address</v-list-item-subtitle>
                  <v-list-item-title>{{
                    firm.Email || "N/A"
                  }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-icon
                  ><v-icon color="primary">mdi-phone</v-icon></v-list-item-icon
                >
                <v-list-item-content>
                  <v-list-item-subtitle>Phone Number</v-list-item-subtitle>
                  <v-list-item-title>{{
                    firm.PhoneNo || "N/A"
                  }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>

            <v-alert
              v-if="shouldPromptLogin"
              type="info"
              text
              border="left"
              class="mt-4"
            >
              <div class="d-flex flex-column">
                <span class="text-body-2 mb-2"
                  >To manage your firm's annual licenses, specialized
                  certifications, and staff updates, please login to the Firm
                  Portal.</span
                >
                <v-btn
                  color="primary"
                  depressed
                  @click="loginToFirm"
                  class="text-capitalize align-self-start"
                >
                  Login to Firm Portal
                  <v-icon right small>mdi-login-variant</v-icon>
                </v-btn>
              </div>
            </v-alert>
          </v-card>
        </v-tab-item>

        <!-- Application Tab -->
        <v-tab-item>
          <v-card flat class="pa-6">
            <div v-if="firm.firmApplication">
              <v-subheader>Application Progress</v-subheader>
              <v-card outlined class="pa-4 mb-4">
                <div class="d-flex justify-space-between align-center mb-2">
                  <span class="text-subtitle-2">Status:</span>
                  <v-chip
                    small
                    :color="statusColor(firm.firmApplication.status)"
                    dark
                  >
                    {{ firm.firmApplication.status }}
                  </v-chip>
                </div>
                <div class="d-flex justify-space-between align-center mb-2">
                  <span class="text-subtitle-2">Applied On:</span>
                  <span>{{
                    formatDate(firm.firmApplication.applicationDate)
                  }}</span>
                </div>
                <v-divider class="my-3"></v-divider>
                <v-card-actions class="pa-0">
                  <v-btn
                    v-if="canMakePayment"
                    block
                    color="success"
                    @click="makePayment"
                    depressed
                  >
                    Make Application Payment
                    <v-icon right>mdi-credit-card-outline</v-icon>
                  </v-btn>
                  <v-btn
                    v-else-if="canResume"
                    block
                    color="warning"
                    @click="resumeApplication"
                    depressed
                  >
                    Complete Application
                    <v-icon right>mdi-arrow-right</v-icon>
                  </v-btn>
                </v-card-actions>
              </v-card>

              <v-subheader>License Documents</v-subheader>
              <v-list v-if="licenses.length > 0" dense>
                <v-list-item v-for="(lic, i) in licenses" :key="i" class="px-0">
                  <v-list-item-icon
                    ><v-icon color="secondary"
                      >mdi-file-certificate</v-icon
                    ></v-list-item-icon
                  >
                  <v-list-item-content>
                    <v-list-item-title>{{
                      lic.LicenseName || "Firm License"
                    }}</v-list-item-title>
                    <v-list-item-subtitle
                      >Issued:
                      {{ formatDate(lic.IssueDate) }}</v-list-item-subtitle
                    >
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn icon @click="downloadFile(lic.DocumentUrl)">
                      <v-icon color="primary">mdi-download</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
              <p v-else class="text-caption grey--text text-center py-4">
                No active licenses found.
              </p>
            </div>
            <div v-else class="text-center py-8">
              <v-icon size="48" color="grey lighten-2"
                >mdi-clipboard-text-outline</v-icon
              >
              <p class="mt-2 grey--text">
                No application details found for this firm.
              </p>
            </div>
          </v-card>
        </v-tab-item>

        <!-- Finance Tab -->
        <v-tab-item>
          <v-card flat class="pa-6">
            <v-subheader class="px-0">Subscriptions</v-subheader>
            <v-simple-table
              dense
              class="mb-6 elevation-1 rounded overflow-hidden"
            >
              <template v-slot:default>
                <thead class="grey lighten-4">
                  <tr>
                    <th>Year</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="sub in subscriptions" :key="sub.id">
                    <td>{{ sub.year }}</td>
                    <td>{{ sub.amount | currency }}</td>
                    <td>
                      <v-chip x-small :color="sub.paid ? 'success' : 'error'">
                        {{ sub.paid ? "Paid" : "Unpaid" }}
                      </v-chip>
                    </td>
                    <td>
                      <v-btn
                        v-if="!sub.paid"
                        x-small
                        color="primary"
                        depressed
                        @click="paySubscription(sub)"
                      >
                        Pay Now
                      </v-btn>
                      <v-icon v-else color="success" small
                        >mdi-check-circle</v-icon
                      >
                    </td>
                  </tr>
                  <tr v-if="subscriptions.length === 0">
                    <td colspan="4" class="text-center grey--text py-4">
                      No subscriptions found.
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>

            <v-subheader class="px-0">Payment Receipts</v-subheader>
            <v-list
              v-if="receipts.length > 0"
              dense
              class="elevation-1 rounded"
            >
              <v-list-item v-for="(receipt, i) in receipts" :key="i">
                <v-list-item-icon
                  ><v-icon color="success"
                    >mdi-file-document-check</v-icon
                  ></v-list-item-icon
                >
                <v-list-item-content>
                  <v-list-item-title>{{ receipt.ReceiptNo }}</v-list-item-title>
                  <v-list-item-subtitle
                    >{{ formatDate(receipt.Date) }} -
                    {{ receipt.Amount | currency }}</v-list-item-subtitle
                  >
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn icon @click="downloadFile(receipt.Url)">
                    <v-icon color="primary">mdi-download</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
            <p v-else class="text-caption grey--text text-center py-4">
              No receipts available.
            </p>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </template>
  </v-navigation-drawer>
</template>

<script>
import DateMixin from "../../../mixins/DateMixin";

export default {
  name: "FirmDetailsDrawer",
  mixins: [DateMixin],
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeTab: 0,
      loading: false,
    };
  },
  computed: {
    drawer: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    loadingGetter() {
      return this.$store.getters["Firms/loadingDetails"];
    },
    firm() {
      return this.$store.getters["Firms/selectedFirmDetails"];
    },
    subscriptions() {
      return this.$store.getters["Firms/firmSubscriptions"] || [];
    },
    receipts() {
      return [];
    },
    licenses() {
      return this.$store.getters["Firms/firmLicenses"] || [];
    },
    shouldPromptLogin() {
      if (!this.firm?.firmApplication) return false;
      const status = this.firm.firmApplication.status;
      return (
        status === "Approved" || status === "Registered" || status === "Active"
      );
    },
    canMakePayment() {
      return this.firm?.firmApplication?.status === "Invoiced";
    },
    canResume() {
      return (
        this.firm?.firmApplication?.status === "New" ||
        this.firm?.firmApplication?.status === "Draft"
      );
    },
  },
  filters: {
    currency(value) {
      if (!value) return "0.00";
      return new Intl.NumberFormat("en-KE", {
        style: "currency",
        currency: "KES",
      }).format(value);
    },
  },
  watch: {
    loadingGetter() {
      this.loading = this.loadingGetter;
      console.log("loading", this.loading, this.firm);
    },
  },
  methods: {
    statusColor(status) {
      switch (status) {
        case "Active":
          return "success";
        case "In Review":
          return "info";
        case "Draft":
        case "New":
          return "warning";
        case "Rejected":
          return "error";
        default:
          return "grey darken-1";
      }
    },
    loginToFirm() {
      if (
        confirm(
          `You are about to login to ${this.firm.firmName} portal. Proceed?`
        )
      ) {
        this.$store.dispatch("Admin/loginAs", {
          profile_id: this.firm.profileID,
        });
      }
    },
    makePayment() {
      this.$router.push({
        name: "Payments",
        query: { ref: this.firm.firmApplication.code },
      });
    },
    resumeApplication() {
      this.$router.push({
        name: "firm-card",
        params: {
          firmCode: this.firm.profileID,
          application: this.firm.firmApplication.code,
        },
      });
    },
    paySubscription(sub) {
      this.$router.push({ name: "Payments", query: { subscription: sub.id } });
    },
    downloadFile(url) {
      if (url) window.open(url, "_blank");
    },
  },
};
</script>

<style scoped>
.firm-details-drawer {
  z-index: 100 !important;
}
.bg-light {
  background-color: #f8f9fa;
}
</style>
